/**
 * Pantalla de escaneo de un conteo abierto (multi-usuario).
 *
 * A diferencia de la pantalla vieja (que acumulaba todo en el cliente y
 * pisaba un borrador-foto con cada autosave), aquí CADA escaneo se envía al
 * servidor (/api/conteos_scan) y el servidor decide: contado, duplicado (y de
 * quién), sobrante o fuera de alcance. Varias personas pueden escanear el
 * mismo conteo a la vez; el avance de los demás llega por polling incremental
 * (/api/conteos_scans cada 4 s con el último id visto).
 *
 * "Actualizar inventario" (/api/conteos_refrescar) vuelve a tomar la foto de
 * la sucursal con el conteo abierto; el polling ve subir `refrescos` y cada
 * dispositivo recarga la lista esperada completa.
 *
 * Red inestable (bodega/WiFi): los escaneos entran a una cola local que se
 * vacía en orden; si la red cae se reintenta sola. El índice único del
 * servidor hace inocuos los reenvíos.
 */
import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import Box from "@mui/material/Box";
import {
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { useNavigate, useSearchParams } from "react-router-dom";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import QrCodeScannerRoundedIcon from "@mui/icons-material/QrCodeScannerRounded";
import KeyboardRoundedIcon from "@mui/icons-material/KeyboardRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ReportProblemRoundedIcon from "@mui/icons-material/ReportProblemRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import CloudOffRoundedIcon from "@mui/icons-material/CloudOffRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import SyncRoundedIcon from "@mui/icons-material/SyncRounded";
import { useGlobalMDAlert } from "context";
import { useMaterialUIController } from "context";
import { FP, StatusPill, fpInputSx } from "components/financepro";
import { useApi } from "lib/api";
import { norm, alcanceLabel, puedeConteos, idDispositivo } from "modules/conteos/comunes";

function unitCode(chasis, tipo) {
  const c = norm(chasis);
  const suffix = c.slice(-4) || c.slice(0, 4) || "0000";
  return tipo === "Incautado" ? `RC${suffix}` : `MT${suffix}`;
}

function truncChasis(chasis) {
  const s = String(chasis ?? "");
  return s.length > 14 ? `${s.slice(0, 14)}…` : s;
}

function CompactStat({ label, value, color = FP.color.textPrimary }) {
  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        textAlign: "center",
        px: 1,
        py: 1.6,
        borderRadius: 2,
        border: `1px solid ${FP.color.border}`,
        bgcolor: FP.color.cardBg,
        boxShadow: FP.shadow.soft,
      }}
    >
      <Typography
        sx={{
          fontWeight: 800,
          fontSize: { xs: "1.45rem", md: "1.6rem" },
          color,
          fontVariantNumeric: "tabular-nums",
          lineHeight: 1.1,
        }}
      >
        {value}
      </Typography>
      <Typography
        sx={{
          mt: 0.5,
          color: FP.color.textSecondary,
          fontWeight: 700,
          fontSize: "0.62rem",
          letterSpacing: "0.12em",
          textTransform: "uppercase",
        }}
      >
        {label}
      </Typography>
    </Box>
  );
}

// Estilos del renglón fuera del componente: con cientos de unidades, recrear
// estos objetos sx en cada render era parte del costo por escaneo.
const unitRowBaseSx = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 1.5,
  px: { xs: 1.75, md: 2 },
  py: 1.5,
  borderRadius: 2,
  boxShadow: FP.shadow.soft,
};
const unitRowPendienteSx = {
  ...unitRowBaseSx,
  border: `1px solid ${FP.color.border}`,
  bgcolor: FP.color.cardBg,
};
const unitRowContadaSx = {
  ...unitRowBaseSx,
  border: "1px solid #BBF7D0",
  bgcolor: FP.color.successSoft,
};

// React.memo: al escanear solo cambia la fila afectada; las demás (cientos)
// no deben re-renderizarse. `item` mantiene identidad estable en `expected`.
const UnitRow = React.memo(function UnitRow({ item, counted, por }) {
  const isRecuperada = item.tipo === "Incautado";
  const desc = [item.marca, item.modelo].filter(Boolean).join(" ");
  const subtitle = desc
    ? `${desc} · chasis ${truncChasis(item.chasis)}`
    : `chasis ${truncChasis(item.chasis)}`;

  return (
    <Box sx={counted ? unitRowContadaSx : unitRowPendienteSx}>
      <Box sx={{ minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: "0.95rem",
              color: FP.color.textPrimary,
              letterSpacing: "0.02em",
            }}
          >
            {unitCode(item.chasis, item.tipo)}
          </Typography>
          {isRecuperada ? (
            <Chip
              label="Recuperada"
              size="small"
              sx={{
                height: 20,
                fontSize: "0.62rem",
                fontWeight: 700,
                bgcolor: FP.color.warningSoft,
                color: "#92400E",
              }}
            />
          ) : null}
        </Box>
        <Typography sx={{ mt: 0.35, color: FP.color.textSecondary, fontSize: "0.8rem" }}>
          {subtitle}
          {counted && por ? ` · por ${por}` : ""}
        </Typography>
      </Box>
      {counted ? (
        <CheckCircleRoundedIcon sx={{ color: FP.color.successDark, fontSize: 22, flexShrink: 0 }} />
      ) : (
        <StatusPill label="Pendiente" variant="neutral" sx={{ flexShrink: 0 }} />
      )}
    </Box>
  );
});

export default function ConteoActivo() {
  const { post } = useApi();
  const { showAlert } = useGlobalMDAlert();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMdDown = useMediaQuery(theme.breakpoints.down("md"));
  const [controller] = useMaterialUIController();
  const { usuarioLog, roleDetalles } = controller;
  const [searchParams] = useSearchParams();
  const conteoId = Number(searchParams.get("id") || 0);
  const puedeCerrar = puedeConteos(roleDetalles, "modificar");

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [cab, setCab] = useState(null); // cabecera del conteo
  const [expected, setExpected] = useState([]);

  // Contadas: [{key, usuario}] en orden de llegada (propias y de los demás).
  const [countedList, setCountedList] = useState([]);
  const [extrasList, setExtrasList] = useState([]);
  const countedSetRef = useRef(new Set());
  const extrasSetRef = useRef(new Set());

  const [manualValue, setManualValue] = useState("");
  const [manualOpen, setManualOpen] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [scannerReady, setScannerReady] = useState(true);

  // Cola de escaneos por enviar (red inestable) y estado de sincronización.
  const queueRef = useRef([]);
  const sendingRef = useRef(false);
  const retryTimerRef = useRef(0);
  const [pendientesEnvio, setPendientesEnvio] = useState(0);
  const [offline, setOffline] = useState(false);

  // Polling incremental: id del último escaneo aplicado.
  const lastIdRef = useRef(0);
  // Veces que se actualizó el inventario esperado; si el polling trae otro
  // valor, alguien lo refrescó y hay que recargar la lista.
  const refrescosRef = useRef(0);
  const [refrescando, setRefrescando] = useState(false);

  // Cierre: diálogo de confirmación propio, o aviso de que otro lo cerró.
  const [confirmarCierre, setConfirmarCierre] = useState(false);
  const [cerrando, setCerrando] = useState(false);
  const [cerradoPor, setCerradoPor] = useState("");

  const inputRef = useRef(null);
  const audioRef = useRef(null);
  const manualOpenRef = useRef(false);
  // Bandera de montaje: evita que el input recupere el foco después de salir de
  // la pantalla (lo que dejaba el teclado bloqueado en toda la app).
  const mountedRef = useRef(true);
  const rafRef = useRef(0);
  // true mientras hay un diálogo abierto, para no pelear el foco con el
  // "focus trap" del diálogo de MUI.
  const suppressFocusRef = useRef(false);
  const dispositivoRef = useRef(idDispositivo());

  const expectedByKey = useMemo(() => {
    const m = new Map();
    expected.forEach((p) => m.set(p.key, p));
    return m;
  }, [expected]);

  const focusInput = useCallback(() => {
    if (!mountedRef.current || manualOpenRef.current || suppressFocusRef.current) return;
    cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      if (!mountedRef.current || manualOpenRef.current || suppressFocusRef.current) return;
      if (inputRef.current && document.body.contains(inputRef.current)) {
        inputRef.current.focus();
      }
    });
  }, []);

  // Limpieza al desmontar: igual que la pantalla vieja — liberar foco/IME
  // ANTES de que el input se desmonte (en tablets un input enfocado que se
  // elimina del DOM deja el teclado virtual colgado en toda la app).
  useEffect(() => {
    return () => {
      mountedRef.current = false;
      cancelAnimationFrame(rafRef.current);
      clearTimeout(retryTimerRef.current);
      const el = inputRef.current;
      if (el && typeof el.blur === "function") {
        try { el.blur(); } catch (e) { /* ignore */ }
      }
      if (document.activeElement && typeof document.activeElement.blur === "function") {
        try { document.activeElement.blur(); } catch (e) { /* ignore */ }
      }
      if (audioRef.current) {
        try { audioRef.current.close(); } catch (e) { /* ignore */ }
        audioRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    suppressFocusRef.current = manualOpen || confirmarCierre || !!cerradoPor;
  }, [manualOpen, confirmarCierre, cerradoPor]);

  const beep = useCallback((freq, durationMs, type = "sine") => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      if (!audioRef.current) audioRef.current = new Ctx();
      const ctx = audioRef.current;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = type;
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.18, ctx.currentTime);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + durationMs / 1000);
    } catch (e) {
      /* ignore */
    }
  }, []);

  const soundOk = useCallback(() => beep(900, 110), [beep]);
  const soundDup = useCallback(() => {
    beep(500, 80);
    setTimeout(() => beep(500, 80), 120);
  }, [beep]);
  const soundExtra = useCallback(() => beep(170, 320, "square"), [beep]);

  const marcarContado = useCallback((key, usuario) => {
    if (countedSetRef.current.has(key)) return;
    countedSetRef.current.add(key);
    setCountedList((prev) => [...prev, { key, usuario }]);
  }, []);

  const marcarSobrante = useCallback((key, chasis, usuario) => {
    if (extrasSetRef.current.has(key)) return;
    extrasSetRef.current.add(key);
    setExtrasList((prev) => [...prev, { key, chasis, usuario }]);
  }, []);

  // ---- estado completo del conteo (al entrar y tras actualizar inventario) ----
  const aplicarEstado = useCallback(
    (data) => {
      const seen = new Set();
      const list = [];
      (data.esperados || []).forEach((p) => {
        const key = norm(p.chasis);
        if (!key || seen.has(key)) return;
        seen.add(key);
        list.push({
          key,
          chasis: String(p.chasis),
          marca: p.marca || "",
          modelo: p.modelo || "",
          colore: p.colore || "",
          tipo: p.tipo || "",
        });
      });

      const contados = [];
      const contadosSet = new Set();
      const extras = [];
      const extrasSet = new Set();
      (data.scans || []).forEach((s) => {
        const key = norm(s.chasis);
        if (!key) return;
        if (s.resultado === "C" && !contadosSet.has(key)) {
          contadosSet.add(key);
          contados.push({ key, usuario: s.usuario || "" });
        } else if (s.resultado === "S" && !extrasSet.has(key)) {
          extrasSet.add(key);
          extras.push({ key, chasis: String(s.chasis), usuario: s.usuario || "" });
        }
      });
      // Lo escaneado que sigue en la cola local todavía no llegó al
      // servidor: se conserva como contado si está en la lista.
      queueRef.current.forEach((q) => {
        if (seen.has(q.key) && !contadosSet.has(q.key)) {
          contadosSet.add(q.key);
          contados.push({ key: q.key, usuario: usuarioLog || "" });
        }
      });

      countedSetRef.current = contadosSet;
      extrasSetRef.current = extrasSet;
      lastIdRef.current = data.ultimo || 0;
      refrescosRef.current = data.refrescos || 0;
      setCab(data);
      setExpected(list);
      setCountedList(contados);
      setExtrasList(extras);
    },
    [usuarioLog]
  );

  // ---- carga inicial (unirse al conteo) ----
  useEffect(() => {
    let cancelled = false;

    async function cargar() {
      if (!conteoId) {
        setLoadError("Conteo no indicado.");
        setLoading(false);
        return;
      }
      setLoading(true);
      setLoadError("");
      try {
        const { ok, data } = await post("/api/conteos_unirse", { conteo_id: conteoId });
        if (cancelled) return;
        if (!ok) {
          setLoadError(data?.Error || "No se pudo cargar el conteo.");
          setLoading(false);
          return;
        }
        if (data.estado !== "A") {
          setLoadError("Este conteo ya no está abierto.");
          setLoading(false);
          return;
        }

        aplicarEstado(data);
        setLoading(false);
        focusInput();
      } catch (err) {
        if (cancelled) return;
        setLoadError("Error cargando el conteo: " + (err?.message || "desconocido"));
        setLoading(false);
      }
    }

    cargar();
    return () => {
      cancelled = true;
    };
  }, [conteoId, post, focusInput, aplicarEstado]);

  // Recarga silenciosa tras "Actualizar inventario" (propio o de otro).
  const recargarEstado = useCallback(async () => {
    try {
      const { ok, data } = await post("/api/conteos_unirse", { conteo_id: conteoId });
      if (!ok || !mountedRef.current || data.estado !== "A") return;
      aplicarEstado(data);
    } catch (err) {
      /* el próximo polling lo vuelve a intentar */
    }
  }, [post, conteoId, aplicarEstado]);

  // ---- cola de envío ----
  const aplicarRespuestaScan = useCallback(
    (item, data) => {
      const key = item.key;
      const r = data?.resultado || "";
      if (r === "S") {
        // Conteo por marca: el chasis desconocido se confirmó como sobrante.
        marcarSobrante(key, item.chasis, usuarioLog || "");
      } else if (r === "D") {
        // Otro dispositivo lo registró primero; prevalece el registro original.
        if (data.original === "S") marcarSobrante(key, item.chasis, data.usuario || "");
        else marcarContado(key, data.usuario || "");
        if (data.usuario && data.usuario !== usuarioLog) {
          setFeedback({ tone: "dup", msg: `Ya lo había escaneado ${data.usuario}: ${key}` });
        }
      } else if (r === "F") {
        const que = [data.marca, data.modelo].filter(Boolean).join(" ");
        setFeedback({
          tone: "extra",
          msg: `Fuera de este conteo (es ${que || "otra unidad"}): ${key}`,
        });
      } else if (r === "X") {
        queueRef.current = [];
        setPendientesEnvio(0);
        setCerradoPor(data.cerrado_por || "otro usuario");
      }
      // "C": ya estaba aplicado de forma optimista al escanear.
    },
    [marcarContado, marcarSobrante, usuarioLog]
  );

  const enviarColaRef = useRef(() => {});
  const enviarCola = useCallback(async () => {
    if (sendingRef.current) return;
    sendingRef.current = true;
    try {
      while (queueRef.current.length > 0 && mountedRef.current) {
        const item = queueRef.current[0];
        let res;
        try {
          res = await post("/api/conteos_scan", {
            conteo_id: conteoId,
            chasis: item.chasis,
            dispositivo: dispositivoRef.current,
          });
        } catch (err) {
          // Red caída: conservar el item y reintentar. El servidor absorbe
          // los reenvíos (índice único), así que reintentar nunca duplica.
          setOffline(true);
          clearTimeout(retryTimerRef.current);
          retryTimerRef.current = setTimeout(() => {
            if (mountedRef.current) enviarColaRef.current();
          }, 4000);
          return;
        }
        setOffline(false);
        queueRef.current.shift();
        setPendientesEnvio(queueRef.current.length);
        if (res.ok) {
          aplicarRespuestaScan(item, res.data);
        } else if (res.data?.Error) {
          showAlert({ message: res.data.Error, color: "error", autoHideDuration: 4000 });
        }
      }
    } finally {
      sendingRef.current = false;
    }
  }, [post, conteoId, aplicarRespuestaScan, showAlert]);

  useEffect(() => {
    enviarColaRef.current = enviarCola;
  }, [enviarCola]);

  const encolar = useCallback(
    (key, chasis) => {
      queueRef.current.push({ key, chasis });
      setPendientesEnvio(queueRef.current.length);
      enviarColaRef.current();
    },
    []
  );

  // ---- polling incremental del avance de los demás ----
  useEffect(() => {
    if (loading || loadError || cerradoPor) return undefined;
    const timer = setInterval(() => {
      post("/api/conteos_scans", { conteo_id: conteoId, desde: lastIdRef.current })
        .then(({ ok, data }) => {
          if (!ok || !mountedRef.current) return;
          (data.scans || []).forEach((s) => {
            lastIdRef.current = Math.max(lastIdRef.current, s.id || 0);
            const key = norm(s.chasis);
            if (!key) return;
            if (s.resultado === "C") marcarContado(key, s.usuario || "");
            else if (s.resultado === "S") marcarSobrante(key, String(s.chasis), s.usuario || "");
          });
          if (data.estado && data.estado !== "A") {
            setCerradoPor(data.cerrado_por || "otro usuario");
          } else if ((data.refrescos || 0) !== refrescosRef.current) {
            refrescosRef.current = data.refrescos || 0;
            recargarEstado();
          }
        })
        .catch(() => {});
    }, 4000);
    return () => clearInterval(timer);
  }, [loading, loadError, cerradoPor, post, conteoId, marcarContado, marcarSobrante, recargarEstado]);

  // ---- escaneo ----
  const procesarScan = useCallback(
    (entrada) => {
      // Con el idioma de teclado del lector distinto al de la tablet, el guion
      // de los chasis (CF46A-596866) llega como "*" o "'". Ningún chasis los lleva.
      const raw = String(entrada ?? "").replace(/[*']/g, "-");
      const key = norm(raw);
      // Input no controlado: se limpia directo en el DOM. Así las ~17 teclas
      // que "escribe" el lector HID por escaneo no re-renderizan la pantalla.
      if (inputRef.current) inputRef.current.value = "";
      setManualValue("");
      if (!key) {
        focusInput();
        return;
      }
      if (countedSetRef.current.has(key)) {
        soundDup();
        setFeedback({ tone: "dup", msg: `Ya estaba contado: ${key}` });
      } else if (expectedByKey.has(key)) {
        // Optimista: se marca contado ya (por mí) y se confirma en la cola.
        marcarContado(key, usuarioLog || "");
        encolar(key, key);
        soundOk();
        const p = expectedByKey.get(key);
        setFeedback({
          tone: "ok",
          msg: `Contado: ${key}${p ? ` · ${[p.marca, p.modelo].filter(Boolean).join(" ")}` : ""}`,
        });
      } else if (extrasSetRef.current.has(key)) {
        soundDup();
        setFeedback({ tone: "dup", msg: `Ya estaba registrado como sobrante: ${key}` });
      } else if ((cab?.alcance || "T") !== "T") {
        // En conteo por marca o por modelo el cliente no sabe si el chasis
        // es de otra marca/modelo de la sucursal (fuera de alcance) o un
        // sobrante real: lo decide el servidor al procesar la cola.
        soundExtra();
        setFeedback({ tone: "extra", msg: `No es de este conteo… verificando: ${key}` });
        encolar(key, String(raw).trim());
      } else {
        soundExtra();
        marcarSobrante(key, String(raw).trim(), usuarioLog || "");
        encolar(key, String(raw).trim());
        setFeedback({ tone: "extra", msg: `Sobrante / no pertenece a esta sucursal: ${key}` });
        showAlert({
          message: `Chasis ${key} no pertenece al inventario esperado de esta sucursal`,
          color: "error",
          autoHideDuration: 3500,
        });
      }
      focusInput();
    },
    [
      expectedByKey,
      cab,
      usuarioLog,
      focusInput,
      soundOk,
      soundDup,
      soundExtra,
      showAlert,
      marcarContado,
      marcarSobrante,
      encolar,
    ]
  );

  const onScanSubmit = (e) => {
    e.preventDefault();
    procesarScan(inputRef.current ? inputRef.current.value : "");
  };

  const abrirManual = (e) => {
    // No dejar que el click burbujee a la tarjeta del escáner (re-enfocaría el
    // input oculto y pelearía con el focus trap del diálogo).
    if (e) e.stopPropagation();
    manualOpenRef.current = true;
    setManualValue("");
    setManualOpen(true);
  };

  const cerrarManual = () => {
    manualOpenRef.current = false;
    setManualOpen(false);
    focusInput();
  };

  const onManualSubmit = (e) => {
    e.preventDefault();
    const val = manualValue;
    manualOpenRef.current = false;
    setManualOpen(false);
    procesarScan(val);
  };

  const countedSet = useMemo(() => new Set(countedList.map((c) => c.key)), [countedList]);
  const usuarioPorKey = useMemo(() => {
    const m = new Map();
    countedList.forEach((c) => m.set(c.key, c.usuario));
    return m;
  }, [countedList]);
  const pendientes = useMemo(
    () => expected.filter((p) => !countedSet.has(p.key)),
    [expected, countedSet]
  );

  const totalEsperado = expected.length;
  const totalContado = countedSet.size;
  const totalPendiente = pendientes.length;
  const totalSobrante = extrasList.length;

  // Avance por persona (propio + los que llegan por polling).
  const participantes = useMemo(() => {
    const m = new Map();
    countedList.forEach((c) => {
      const u = c.usuario || "¿?";
      m.set(u, (m.get(u) || 0) + 1);
    });
    return [...m.entries()].sort((a, b) => b[1] - a[1]);
  }, [countedList]);

  // Con cientos de unidades no se pinta todo: pendientes por tramos de a 100
  // y contadas colapsadas (solo las últimas escaneadas al expandir).
  const [maxPendientes, setMaxPendientes] = useState(100);
  const [verContadas, setVerContadas] = useState(false);
  const contadasRecientes = useMemo(
    () =>
      countedList
        .slice(-50)
        .reverse()
        .map((c) => ({ item: expectedByKey.get(c.key), usuario: c.usuario }))
        .filter((c) => c.item),
    [countedList, expectedByKey]
  );

  function salir() {
    // Liberar el foco/IME del input del escáner ANTES de navegar para que el
    // teclado no quede bloqueado al salir.
    if (inputRef.current && typeof inputRef.current.blur === "function") {
      try { inputRef.current.blur(); } catch (e) { /* ignore */ }
    }
    navigate("/inventario/conteos");
  }

  function actualizarInventario() {
    setRefrescando(true);
    post("/api/conteos_refrescar", { conteo_id: conteoId })
      .then(async ({ ok, data }) => {
        if (!ok) {
          setRefrescando(false);
          showAlert({
            message: data?.Error || "No se pudo actualizar el inventario",
            color: "error",
            autoHideDuration: 4000,
          });
          return;
        }
        await recargarEstado();
        setRefrescando(false);
        const partes = [];
        if (data.agregados) partes.push(`${data.agregados} agregadas`);
        if (data.quitados) partes.push(`${data.quitados} quitadas`);
        if (data.reclasificados) partes.push(`${data.reclasificados} sobrantes pasaron a contadas`);
        showAlert({
          message: partes.length
            ? `Inventario actualizado: ${partes.join(" · ")}.`
            : "El inventario no cambió desde que se abrió.",
          color: "success",
          autoHideDuration: 4000,
        });
        focusInput();
      })
      .catch((err) => {
        setRefrescando(false);
        showAlert({ message: "Error de red: " + err.message, color: "error", autoHideDuration: 4000 });
      });
  }

  function cerrarConteo() {
    setCerrando(true);
    post("/api/conteos_cerrar", { conteo_id: conteoId })
      .then(({ ok, data }) => {
        setCerrando(false);
        if (!ok) {
          showAlert({
            message: data?.Error || "No se pudo cerrar el conteo",
            color: "error",
            autoHideDuration: 4000,
          });
          return;
        }
        setConfirmarCierre(false);
        showAlert({
          message: "Conteo cerrado y reconciliado.",
          color: "success",
          autoHideDuration: 3500,
        });
        salir();
      })
      .catch((err) => {
        setCerrando(false);
        showAlert({ message: "Error de red: " + err.message, color: "error", autoHideDuration: 4000 });
      });
  }

  const feedbackStyles = {
    ok: { bg: FP.color.successSoft, color: FP.color.successDark, icon: CheckCircleRoundedIcon },
    dup: { bg: FP.color.warningSoft, color: FP.color.warning, icon: ReportProblemRoundedIcon },
    extra: { bg: FP.color.dangerSoft, color: FP.color.danger, icon: ReportProblemRoundedIcon },
  };

  const tituloSucursal = cab
    ? `${cab.sucursal || ""}${cab.descripcion ? `-${cab.descripcion}` : ""}`
    : "";

  return (
    <Box
      component="main"
      sx={{
        minHeight: "100vh",
        bgcolor: FP.color.surface,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          pl: { xs: 1.5, md: 3 },
          pr: { xs: 2, md: 3 },
          py: { xs: 1.75, md: 2 },
          bgcolor: FP.color.cardBg,
          borderBottom: `1px solid ${FP.color.border}`,
          position: "sticky",
          top: 0,
          zIndex: 20,
        }}
      >
        <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={2}>
          <Box display="flex" alignItems="flex-start" gap={1.2} minWidth={0}>
            <IconButton
              size="small"
              onClick={salir}
              sx={{
                mt: 0.2,
                color: FP.color.textSecondary,
                border: `1px solid ${FP.color.border}`,
                borderRadius: 1.4,
                width: 36,
                height: 36,
                "&:hover": { bgcolor: FP.color.surfaceAlt, color: FP.color.navy },
              }}
            >
              <ArrowBackRoundedIcon sx={{ fontSize: 20 }} />
            </IconButton>
            <Box minWidth={0}>
              <Typography
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: "1.15rem", md: "1.25rem" },
                  color: FP.color.textPrimary,
                  letterSpacing: "-0.01em",
                  lineHeight: 1.2,
                }}
              >
                Conteo #{conteoId} · {cab ? alcanceLabel(cab) : ""}
              </Typography>
              <Typography
                sx={{
                  mt: 0.35,
                  fontSize: "0.72rem",
                  fontWeight: 700,
                  letterSpacing: "0.1em",
                  textTransform: "uppercase",
                  color: FP.color.textSecondary,
                }}
              >
                {tituloSucursal}
                {cab?.abierto_por ? ` · abierto por ${cab.abierto_por}` : ""}
              </Typography>
            </Box>
          </Box>
          <Box display="flex" alignItems="center" gap={1} flexShrink={0}>
            {puedeCerrar && cab ? (
              <Button
                size="small"
                variant="outlined"
                onClick={actualizarInventario}
                disabled={refrescando || pendientesEnvio > 0}
                startIcon={
                  refrescando ? <CircularProgress size={14} /> : <SyncRoundedIcon sx={{ fontSize: 18 }} />
                }
                sx={{
                  height: 34,
                  borderColor: FP.color.borderStrong,
                  color: FP.color.navy,
                  borderRadius: 1.4,
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: "0.78rem",
                  whiteSpace: "nowrap",
                  "& .MuiButton-startIcon": { mr: { xs: 0, sm: 0.75 } },
                  minWidth: { xs: 36, sm: 0 },
                  px: { xs: 1, sm: 1.5 },
                }}
              >
                <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
                  Actualizar inventario
                </Box>
              </Button>
            ) : null}
            {offline || pendientesEnvio > 0 ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  px: 1.2,
                  py: 0.6,
                  borderRadius: 999,
                  border: `1px solid ${offline ? "#FECACA" : FP.color.border}`,
                  bgcolor: offline ? FP.color.dangerSoft : FP.color.warningSoft,
                }}
              >
                <CloudOffRoundedIcon
                  sx={{ fontSize: 14, color: offline ? FP.color.danger : "#92400E" }}
                />
                <Typography
                  sx={{
                    fontSize: "0.72rem",
                    fontWeight: 700,
                    color: offline ? FP.color.danger : "#92400E",
                    whiteSpace: "nowrap",
                  }}
                >
                  {offline ? "Sin conexión" : "Sincronizando"} · {pendientesEnvio}
                </Typography>
              </Box>
            ) : (
              <Box
                sx={{
                  display: { xs: "none", sm: "flex" },
                  alignItems: "center",
                  gap: 0.75,
                  px: 1.2,
                  py: 0.6,
                  borderRadius: 999,
                  border: `1px solid ${scannerReady ? "#BBF7D0" : FP.color.border}`,
                  bgcolor: scannerReady ? FP.color.successSoft : FP.color.surfaceAlt,
                }}
              >
                <FiberManualRecordIcon
                  sx={{
                    fontSize: 10,
                    color: scannerReady ? FP.color.successDark : FP.color.textMuted,
                  }}
                />
                <Typography
                  sx={{
                    fontSize: "0.72rem",
                    fontWeight: 700,
                    color: scannerReady ? FP.color.successDark : FP.color.textSecondary,
                    whiteSpace: "nowrap",
                  }}
                >
                  Escáner {scannerReady ? "listo" : "inactivo"}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          flex: 1,
          p: { xs: 1.5, sm: 2, md: 3 },
          pb: { xs: 12, md: 10 },
          maxWidth: 960,
          width: "100%",
          mx: "auto",
        }}
      >
        {loading ? (
          <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={10} gap={2}>
            <CircularProgress sx={{ color: FP.color.navy }} />
            <Typography sx={{ color: FP.color.textSecondary }}>Cargando el conteo…</Typography>
          </Box>
        ) : loadError ? (
          <Card
            elevation={0}
            sx={{
              borderRadius: 2.5,
              border: `1px solid ${FP.color.border}`,
              bgcolor: FP.color.cardBg,
            }}
          >
            <CardContent>
              <Box display="flex" flexDirection="column" alignItems="center" py={6} gap={1.5}>
                <ReportProblemRoundedIcon sx={{ fontSize: 48, color: FP.color.danger }} />
                <Typography sx={{ color: FP.color.textPrimary, fontWeight: 700, textAlign: "center" }}>
                  {loadError}
                </Typography>
                <Button
                  onClick={salir}
                  sx={{ textTransform: "none", fontWeight: 700, color: FP.color.navy }}
                >
                  Volver a la lista de conteos
                </Button>
              </Box>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Resumen */}
            <Box sx={{ display: "flex", gap: 1.2, mb: 1.5 }}>
              <CompactStat label="Esperadas" value={String(totalEsperado)} />
              <CompactStat label="Contadas" value={String(totalContado)} color={FP.color.successDark} />
              <CompactStat label="Faltan" value={String(totalPendiente)} color={FP.color.danger} />
              <CompactStat label="Extra" value={String(totalSobrante)} color={FP.color.warning} />
            </Box>

            {/* Quién va contando qué (multi-usuario) */}
            {participantes.length > 0 ? (
              <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 0.6, mb: 2.5 }}>
                <GroupsRoundedIcon sx={{ fontSize: 18, color: FP.color.textSecondary }} />
                {participantes.map(([usuario, n]) => (
                  <Chip
                    key={usuario}
                    label={`${usuario === usuarioLog ? "Tú" : usuario}: ${n}`}
                    size="small"
                    sx={{
                      height: 22,
                      fontWeight: 700,
                      fontSize: "0.68rem",
                      bgcolor: usuario === usuarioLog ? FP.color.successSoft : FP.color.surfaceAlt,
                      color: usuario === usuarioLog ? FP.color.successDark : FP.color.textSecondary,
                    }}
                  />
                ))}
              </Box>
            ) : (
              <Box sx={{ mb: 1 }} />
            )}

            {/* Escáner */}
            <Card
              elevation={0}
              component="form"
              onSubmit={onScanSubmit}
              onClick={focusInput}
              sx={{
                mb: 2.5,
                borderRadius: 2.5,
                border: `1px solid ${FP.color.borderStrong}`,
                bgcolor: FP.color.navy,
                boxShadow: FP.shadow.navy,
                cursor: "text",
                position: "relative",
                overflow: "hidden",
              }}
            >
              <CardContent
                sx={{
                  p: { xs: 2, md: 2.5 },
                  display: "flex",
                  alignItems: "center",
                  gap: 1.75,
                  "&:last-child": { pb: { xs: 2, md: 2.5 } },
                }}
              >
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 1.6,
                    bgcolor: "rgba(255,255,255,0.1)",
                    color: "#FFFFFF",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <QrCodeScannerRoundedIcon sx={{ fontSize: 26 }} />
                </Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography sx={{ fontWeight: 800, color: "#FFFFFF", fontSize: "1.02rem" }}>
                    Escanea una moto
                  </Typography>
                  <Typography sx={{ mt: 0.35, color: "rgba(255,255,255,0.72)", fontSize: "0.82rem" }}>
                    Apunta el lector al código de barras de la etiqueta
                  </Typography>
                </Box>
                <Button
                  onClick={abrirManual}
                  startIcon={<KeyboardRoundedIcon sx={{ fontSize: 18 }} />}
                  sx={{
                    flexShrink: 0,
                    textTransform: "none",
                    fontWeight: 700,
                    fontSize: "0.8rem",
                    color: "#FFFFFF",
                    border: "1px solid rgba(255,255,255,0.35)",
                    borderRadius: 1.6,
                    px: 1.5,
                    py: 0.8,
                    whiteSpace: "nowrap",
                    "&:hover": { bgcolor: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.55)" },
                  }}
                >
                  Digitar
                </Button>
                <TextField
                  inputRef={inputRef}
                  defaultValue=""
                  onFocus={() => setScannerReady(true)}
                  onBlur={isMdDown ? () => setScannerReady(false) : focusInput}
                  autoFocus
                  autoComplete="off"
                  tabIndex={0}
                  inputProps={{
                    // El lector Bluetooth (HID) escribe como teclado real; con
                    // inputMode "none" evitamos que aparezca el teclado virtual y
                    // que su IME quede colgado al salir de la pantalla.
                    inputMode: "none",
                    autoCapitalize: "characters",
                    autoCorrect: "off",
                    spellCheck: false,
                    enterKeyHint: "done",
                    "aria-label": "Escaneo de chasis",
                  }}
                  sx={{
                    position: "absolute",
                    opacity: 0,
                    width: 1,
                    height: 1,
                    pointerEvents: "none",
                    "& .MuiOutlinedInput-root": { p: 0 },
                  }}
                />
              </CardContent>
            </Card>

            {feedback ? (
              <Box
                sx={{
                  mb: 2.5,
                  px: 1.75,
                  py: 1.25,
                  borderRadius: 1.6,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  bgcolor: feedbackStyles[feedback.tone].bg,
                  color: feedbackStyles[feedback.tone].color,
                  fontWeight: 700,
                  fontSize: "0.88rem",
                }}
              >
                {React.createElement(feedbackStyles[feedback.tone].icon, { sx: { fontSize: 20 } })}
                {feedback.msg}
              </Box>
            ) : null}

            {/* Unidades pendientes */}
            <Typography
              sx={{
                mb: 1.2,
                color: FP.color.textSecondary,
                fontWeight: 700,
                fontSize: "0.68rem",
                letterSpacing: "0.14em",
                textTransform: "uppercase",
              }}
            >
              Pendientes ({totalPendiente})
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.2, mb: 3 }}>
              {totalEsperado === 0 ? (
                <Box
                  sx={{
                    py: 5,
                    textAlign: "center",
                    borderRadius: 2,
                    border: `1px dashed ${FP.color.border}`,
                    bgcolor: FP.color.cardBg,
                  }}
                >
                  <Typography sx={{ color: FP.color.textSecondary }}>
                    No hay unidades en el alcance de este conteo.
                  </Typography>
                </Box>
              ) : totalPendiente === 0 ? (
                <Box
                  sx={{
                    py: 5,
                    textAlign: "center",
                    borderRadius: 2,
                    border: `1px dashed #BBF7D0`,
                    bgcolor: FP.color.successSoft,
                  }}
                >
                  <Typography sx={{ color: FP.color.successDark, fontWeight: 700 }}>
                    Todo el inventario esperado fue contado.
                  </Typography>
                </Box>
              ) : (
                <>
                  {pendientes.slice(0, maxPendientes).map((item) => (
                    <UnitRow key={item.key} item={item} counted={false} />
                  ))}
                  {totalPendiente > maxPendientes ? (
                    <Button
                      onClick={() => setMaxPendientes((m) => m + 100)}
                      sx={{
                        textTransform: "none",
                        fontWeight: 700,
                        color: FP.color.navy,
                        border: `1px dashed ${FP.color.border}`,
                        borderRadius: 2,
                        py: 1.2,
                      }}
                    >
                      Mostrar 100 más ({totalPendiente - maxPendientes} pendientes sin mostrar)
                    </Button>
                  ) : null}
                </>
              )}
            </Box>

            {/* Contadas, colapsadas: solo el resumen; al expandir, las últimas escaneadas */}
            {totalContado > 0 ? (
              <Box sx={{ mb: totalSobrante > 0 ? 3 : 0 }}>
                <Box
                  onClick={() => setVerContadas((v) => !v)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                    mb: verContadas ? 1.2 : 0,
                    px: { xs: 1.75, md: 2 },
                    py: 1.5,
                    borderRadius: 2,
                    border: `1px solid #BBF7D0`,
                    bgcolor: FP.color.successSoft,
                    cursor: "pointer",
                  }}
                >
                  <Typography sx={{ fontWeight: 800, fontSize: "0.9rem", color: FP.color.successDark }}>
                    Contadas ({totalContado})
                  </Typography>
                  <Typography sx={{ fontWeight: 700, fontSize: "0.78rem", color: FP.color.successDark }}>
                    {verContadas ? "Ocultar" : "Ver últimas"}
                  </Typography>
                </Box>
                {verContadas ? (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1.2 }}>
                    {contadasRecientes.map(({ item, usuario }) => (
                      <UnitRow key={item.key} item={item} counted por={usuario} />
                    ))}
                    {totalContado > contadasRecientes.length ? (
                      <Typography sx={{ color: FP.color.textSecondary, fontSize: "0.78rem", textAlign: "center" }}>
                        Se muestran las últimas {contadasRecientes.length} escaneadas; el conteo
                        completo queda en el historial al cerrar.
                      </Typography>
                    ) : null}
                  </Box>
                ) : null}
              </Box>
            ) : null}

            {totalSobrante > 0 ? (
              <>
                <Typography
                  sx={{
                    mb: 1.2,
                    color: FP.color.danger,
                    fontWeight: 700,
                    fontSize: "0.68rem",
                    letterSpacing: "0.14em",
                    textTransform: "uppercase",
                  }}
                >
                  Unidades extra
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.2 }}>
                  {extrasList.map((row) => (
                    <Box
                      key={row.key}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 1.5,
                        px: { xs: 1.75, md: 2 },
                        py: 1.5,
                        borderRadius: 2,
                        border: `1px solid ${FP.color.dangerSoft}`,
                        borderLeft: `4px solid ${FP.color.danger}`,
                        bgcolor: FP.color.cardBg,
                        boxShadow: FP.shadow.soft,
                      }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          sx={{
                            fontWeight: 800,
                            fontSize: "0.95rem",
                            color: FP.color.textPrimary,
                            wordBreak: "break-all",
                          }}
                        >
                          {row.chasis}
                        </Typography>
                        <Typography sx={{ mt: 0.35, color: FP.color.danger, fontSize: "0.8rem", fontWeight: 600 }}>
                          No pertenece al inventario esperado
                          {row.usuario ? ` · por ${row.usuario === usuarioLog ? "ti" : row.usuario}` : ""}
                        </Typography>
                      </Box>
                      <StatusPill label="Extra" variant="danger" sx={{ flexShrink: 0 }} />
                    </Box>
                  ))}
                </Box>
              </>
            ) : null}
          </>
        )}
      </Box>

      {/* Footer fijo */}
      {!loading && !loadError ? (
        <Box
          sx={{
            position: "fixed",
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 30,
            px: { xs: 1.5, md: 3 },
            py: 1.5,
            bgcolor: FP.color.cardBg,
            borderTop: `1px solid ${FP.color.border}`,
            boxShadow: "0 -4px 20px rgba(15,23,42,0.06)",
          }}
        >
          <Box
            sx={{
              maxWidth: 960,
              mx: "auto",
              display: "flex",
              gap: 1.2,
            }}
          >
            <Button
              variant="outlined"
              onClick={salir}
              fullWidth
              sx={{
                height: 48,
                borderColor: FP.color.borderStrong,
                color: FP.color.navy,
                bgcolor: FP.color.surfaceAlt,
                borderRadius: 1.6,
                textTransform: "none",
                fontWeight: 700,
                fontSize: "0.88rem",
                "&:hover": { bgcolor: FP.color.surface, borderColor: FP.color.navyMuted },
              }}
            >
              Salir (el conteo sigue abierto)
            </Button>
            {puedeCerrar ? (
              <Button
                variant="contained"
                startIcon={<SaveRoundedIcon />}
                onClick={() => setConfirmarCierre(true)}
                disabled={cerrando || pendientesEnvio > 0}
                fullWidth
                sx={{
                  height: 48,
                  bgcolor: FP.color.successDark,
                  color: "#FFFFFF",
                  borderRadius: 1.6,
                  textTransform: "none",
                  fontWeight: 800,
                  fontSize: "0.88rem",
                  boxShadow: "none",
                  "&:hover": { bgcolor: "#036046", boxShadow: "none" },
                  "&.Mui-disabled": { bgcolor: FP.color.successSoft, color: FP.color.textMuted },
                }}
              >
                {pendientesEnvio > 0 ? "Sincronizando…" : "Cerrar y reconciliar"}
              </Button>
            ) : null}
          </Box>
        </Box>
      ) : null}

      {/* Confirmación de cierre */}
      <Dialog
        open={confirmarCierre}
        onClose={() => {
          setConfirmarCierre(false);
          focusInput();
        }}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 2.5, border: `1px solid ${FP.color.border}` } }}
      >
        <DialogTitle sx={{ fontWeight: 800, color: FP.color.textPrimary, pb: 1 }}>
          Cerrar el conteo
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: FP.color.textSecondary, fontSize: "0.9rem" }}>
            El cierre aplica para TODOS los dispositivos conectados a este conteo.
          </Typography>
          <Typography sx={{ color: FP.color.textPrimary, fontSize: "0.95rem", fontWeight: 700, mt: 1 }}>
            {totalContado} contadas de {totalEsperado}
            {totalPendiente > 0 ? ` · faltan ${totalPendiente}` : ""}
            {totalSobrante > 0 ? ` · ${totalSobrante} sobrantes` : ""}
          </Typography>
          {totalPendiente > 0 ? (
            <Typography sx={{ color: FP.color.danger, fontSize: "0.88rem", fontWeight: 600, mt: 1 }}>
              Las {totalPendiente} unidades no escaneadas quedarán como faltantes.
            </Typography>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => {
              setConfirmarCierre(false);
              focusInput();
            }}
            sx={{ textTransform: "none", fontWeight: 700, color: FP.color.textSecondary }}
          >
            Seguir contando
          </Button>
          <Button
            variant="contained"
            onClick={cerrarConteo}
            disabled={cerrando}
            sx={{
              textTransform: "none",
              fontWeight: 800,
              bgcolor: FP.color.successDark,
              boxShadow: "none",
              "&:hover": { bgcolor: "#036046", boxShadow: "none" },
            }}
          >
            {cerrando ? "Cerrando…" : "Cerrar conteo"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Otro usuario cerró el conteo mientras esta pantalla estaba abierta */}
      <Dialog
        open={!!cerradoPor}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 2.5, border: `1px solid ${FP.color.border}` } }}
      >
        <DialogTitle sx={{ fontWeight: 800, color: FP.color.textPrimary, pb: 1 }}>
          Conteo cerrado
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: FP.color.textSecondary, fontSize: "0.9rem" }}>
            {cerradoPor && cerradoPor !== usuarioLog
              ? `${cerradoPor} cerró este conteo.`
              : "Este conteo fue cerrado."}{" "}
            El resultado quedó en el historial.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="contained"
            onClick={salir}
            sx={{
              textTransform: "none",
              fontWeight: 800,
              bgcolor: FP.color.navy,
              boxShadow: "none",
              "&:hover": { bgcolor: FP.color.navyDeep, boxShadow: "none" },
            }}
          >
            Volver a la lista
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo entrada manual (botón "Digitar" en la tarjeta del escáner) */}
      <Dialog
        open={manualOpen}
        onClose={cerrarManual}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 2.5, border: `1px solid ${FP.color.border}` } }}
      >
        <DialogTitle sx={{ fontWeight: 800, color: FP.color.textPrimary, pb: 1 }}>
          Entrada manual
        </DialogTitle>
        <Box component="form" onSubmit={onManualSubmit}>
          <DialogContent sx={{ pt: 0 }}>
            <Typography sx={{ color: FP.color.textSecondary, fontSize: "0.88rem", mb: 2 }}>
              Digite el chasis si no puede escanearlo con la pistola.
            </Typography>
            <TextField
              value={manualValue}
              onChange={(e) => setManualValue(e.target.value)}
              fullWidth
              autoFocus
              label="Chasis"
              placeholder="Ej. 9C2KC1670…"
              InputLabelProps={{ shrink: true }}
              inputProps={{
                autoCapitalize: "characters",
                autoCorrect: "off",
                spellCheck: false,
              }}
              sx={fpInputSx}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5, pt: 0, gap: 1 }}>
            <Button
              onClick={cerrarManual}
              sx={{ textTransform: "none", fontWeight: 700, color: FP.color.textSecondary }}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="contained"
              sx={{
                textTransform: "none",
                fontWeight: 700,
                bgcolor: FP.color.navy,
                boxShadow: "none",
                "&:hover": { bgcolor: FP.color.navyDeep, boxShadow: "none" },
              }}
            >
              Registrar
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </Box>
  );
}
